import type { SupabaseClient } from '@supabase/supabase-js';
import type { BillingCycle, PlanCode } from '../../../interfaces/src';

/** Accepted values for the two narrowings in `mapManualRequest` below. Kept
 *  local (rather than imported from libs/domain) because the adapter must not
 *  depend on the domain layer — these mirror the DB CHECK constraints. */
const PLAN_CODES: readonly PlanCode[] = [
  'essential',
  'commerce',
  'multi_store',
  'lyfty_light',
];
const BILLING_CYCLE_VALUES: readonly BillingCycle[] = [
  'monthly',
  'quarterly',
  'semiannual',
  'yearly',
];

/** Mirrors of the `tenant_followups` / `tenant_notes` CHECK constraints. Kept
 *  local for the same reason as the two lists above: the adapter narrows what
 *  the database returns without depending on libs/domain. */
const CLIENT_STAGE_VALUES = [
  'new',
  'contacted',
  'onboarding',
  'demo',
  'negotiation',
  'won',
  'lost',
  'dormant',
] as const;
const CLIENT_NOTE_KIND_VALUES = [
  'note',
  'call',
  'whatsapp',
  'email',
  'meeting',
  'demo',
  'visit',
] as const;
const CLIENT_CALL_OUTCOME_VALUES = [
  'answered',
  'no_answer',
  'busy',
  'callback',
  'voicemail',
  'refused',
  'wrong_number',
] as const;
import type {
  BroadcastAudienceEntry,
  BroadcastSendResult,
  PlatformBroadcast,
  SendBroadcastInput,
  SendBroadcastTestInput,
  IPlatformAdminRepository,
  PlatformAdminDashboard,
  PlatformAdminOnboardingProfile,
  PlatformAdminTenantActivity,
  PlatformAdminTenantPageStat,
  PlatformAdminTenantJournal,
  PlatformAdminTenantNote,
  PlatformAdminTenantFollowUp,
  PlatformAdminTenantCrmLink,
  CreatePlatformAdminTenantNoteInput,
  SavePlatformAdminTenantFollowUpInput,
  PlatformAdminMonthlyRevenue,
  PlatformAdminLandingUsage,
  PlatformAdminModuleState,
  PlatformAdminSupabaseUsage,
  PlatformAdminTenant,
  PlatformAdminTenantViewAccess,
  PlatformAdminTenantViewHandoff,
  PlatformAdminTotals,
  PlatformAdminUser,
  UpdatePlatformTenantInput,
  PlatformAdminAiOverview,
  PlatformAdminAiRequest,
  PlatformAdminSystemOverview,
  PlatformAdminDeployments,
  PlatformAdminLiveDeployments,
  PlatformAdminDeploymentDetail,
  PlatformAdminUptime,
  PlatformAdminErrors,
  ErrorGroupDetail,
  PlatformAdminAudit,
  PlatformAdminEmailEvents,
  ClientErrorReport,
  MonitoringRange,
  ErrorSource,
  PlatformAdminEdgeFunctions,
  ErrorStatus,
  BlogPost,
  UpsertBlogPostInput,
  BetaProgramConfig,
  PlatformAdminBetaOverview,
  PlatformAdminBetaSignup,
  UpdateBetaConfigInput,
  PlatformAdminAudienceAnalytics,
  AudienceCityRevenueRow,
  AudienceSurface,
  PlatformAdminSearchConsole,
  PlatformAdminSupportThread,
  PlatformAdminSupportMessage,
  PlatformAdminSupportAttachment,
  PlatformAdminSupportNote,
  PlatformAdminSupportFilters,
  PlatformAdminSupportTicketPatch,
  AppFeedbackItem,
  AppFeedbackSummary,
  AppFeedbackFilters,
  AppFeedbackPatch,
  AdminCarrierRequest,
  AdminCarrierRequestFilters,
  AdminCarrierRequestPatch,
  AdminCarrierRequestProvider,
  PlatformAdminSections,
  PlatformAdminRole,
  UpsertPlatformAdminRoleInput,
  PlatformAdminTeamMember,
  PlatformAdminTeamAddResult,
  PlatformAdminTeamInviteResult,
  PlatformAdminInvitation,
  PlatformManualSubscriptionSettings,
  UpdatePlatformManualSubscriptionSettings,
  PlatformManualSubscriptionRequest,
  ReviewManualSubscriptionRequestInput,
  GrantComplimentaryAccessInput,
  ConvertLegacyProToTrialInput,
} from '../../../interfaces/src';
import { rollupByGovernorate } from '@erp/domain/storefront';
import { storedOnboardingProfileSchema } from '@erp/domain/onboarding';

type RpcClient = {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: SupabaseError | null }>;
};

type SupabaseError = {
  code?: string;
  details?: string;
  hint?: string;
  message: string;
};

async function readPlatformAdminFunctionError(
  error: unknown,
  fallback: string,
): Promise<Error> {
  const response = (error as { context?: Response })?.context;
  if (response && typeof response.clone === 'function') {
    try {
      const body = (await response.clone().json()) as { error?: unknown };
      if (typeof body.error === 'string' && body.error.trim()) {
        return new Error(body.error);
      }
    } catch {
      // Fall through to the client error below when the response is not JSON.
    }
  }

  return error instanceof Error && error.message ? error : new Error(fallback);
}

// Empty shapes are reserved for explicitly unavailable external providers.
// A missing database RPC is an operational error: returning a successful zero
// would make the admin console present fabricated healthy/empty production data.
const EMPTY_LIVE_DEPLOYMENTS: PlatformAdminLiveDeployments = {
  generatedAt: '',
  available: false,
  deployments: [],
  runs: [],
};
const EMPTY_DEPLOYMENT_DETAIL: PlatformAdminDeploymentDetail = {
  generatedAt: '',
  available: false,
  run: null,
  jobs: [],
};
// Fresh empty surface each call so site/storefront never share array refs.
const emptyAudienceSurface = (): AudienceSurface => ({
  kpis: {
    visits: 0,
    uniqueVisitors: 0,
    visitsPrev: 0,
    uniqueVisitorsPrev: 0,
    visitsDeltaPct: null,
    uniqueVisitorsDeltaPct: null,
  },
  series: [],
  referrers: [],
  devices: [],
  countries: [],
  topPaths: [],
  utmCampaigns: [],
});

export class SupabasePlatformAdminRepository implements IPlatformAdminRepository {
  constructor(private supabase: SupabaseClient) {}

  async isAdmin(): Promise<boolean> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_is_admin',
      {},
    );
    if (error) {
      if (isMissingRpc(error)) return this.isAdminFromExistingRpc();
      throw error;
    }
    return data === true;
  }

  async isTenantViewActive(): Promise<boolean> {
    // Header-independent admin signal: platform_admin_tenant_view_active() is
    // TRUE iff platform_admin_requested_tenant_id() resolves (server-verified
    // admin_users membership AND a real tenant named in the tenant-view header).
    // Distinct from platform_admin_is_admin(), which the same header suppresses.
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_tenant_view_active',
      {},
    );
    if (error) {
      // Migration not yet applied → treat as "no tenant view" (fail closed).
      if (isMissingRpc(error)) return false;
      throw error;
    }
    return data === true;
  }

  async getDashboard(): Promise<PlatformAdminDashboard> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_dashboard',
      {},
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_dashboard');
      throw error;
    }
    const withUsage = await this.withUsage(mapDashboard(asRecord(data), true));
    const withAccess = await this.withSubscriptionAccess(withUsage);
    return this.withLandingUsage(withAccess);
  }

  async listOnboardingProfiles(): Promise<PlatformAdminOnboardingProfile[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_onboarding_profiles',
      {},
    );
    if (error) {
      if (isMissingRpc(error)) {
        throw missingPlatformAdminCapability(
          'platform_admin_onboarding_profiles',
        );
      }
      throw error;
    }
    const profiles: PlatformAdminOnboardingProfile[] = [];
    for (const item of readArray(data)) {
      const profile = mapOnboardingProfile(asRecord(item));
      if (profile) profiles.push(profile);
    }
    return profiles;
  }

  async getTenantActivity(
    tenantId: string,
  ): Promise<PlatformAdminTenantActivity> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_tenant_activity',
      { p_tenant_id: tenantId },
    );
    if (error) {
      if (isMissingRpc(error)) {
        throw missingPlatformAdminCapability('platform_admin_tenant_activity');
      }
      throw error;
    }
    const record = asRecord(data);
    const pages: PlatformAdminTenantPageStat[] = [];
    for (const item of readArray(record.pages)) {
      const page = asRecord(item);
      const route = readString(page.route);
      if (!route) continue;
      pages.push({
        route,
        surface: readString(page.surface) ?? 'web',
        views: readNumber(page.views),
        lastSeenAt: readString(page.lastSeenAt),
        firstSeenAt: readString(page.firstSeenAt),
      });
    }
    const daily: { day: string; views: number }[] = [];
    for (const item of readArray(record.daily)) {
      const entry = asRecord(item);
      const day = readString(entry.day);
      if (!day) continue;
      daily.push({ day, views: readNumber(entry.views) });
    }
    return {
      tenantId,
      firstSeenAt: readString(record.firstSeenAt),
      lastSeenAt: readString(record.lastSeenAt),
      activeDays: readNumber(record.activeDays),
      pages,
      daily,
    };
  }

  async getTenantJournal(
    tenantId: string,
  ): Promise<PlatformAdminTenantJournal> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_tenant_followup',
      { p_tenant_id: tenantId },
    );
    if (error) {
      if (isMissingRpc(error)) {
        throw missingPlatformAdminCapability('platform_admin_tenant_followup');
      }
      throw error;
    }
    const record = asRecord(data);
    const notes: PlatformAdminTenantNote[] = [];
    for (const item of readArray(record.notes)) {
      const note = mapTenantNote(asRecord(item));
      if (note) notes.push(note);
    }
    return {
      followUp: mapTenantFollowUp(asRecord(record.followUp)),
      notes,
      crmContact: mapTenantCrmLink(asRecord(record.crmContact)),
    };
  }

  async addTenantNote(
    input: CreatePlatformAdminTenantNoteInput,
  ): Promise<PlatformAdminTenantNote> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_tenant_note_add',
      {
        p_tenant_id: input.tenantId,
        p_body: input.body,
        p_kind: input.kind ?? 'note',
        // The DB only accepts an outcome on a call; sending one with any other
        // kind would trip the CHECK constraint instead of being ignored.
        p_outcome: input.kind === 'call' ? (input.outcome ?? null) : null,
        p_happened_at: input.happenedAt ?? null,
      },
    );
    if (error) {
      if (isMissingRpc(error)) {
        throw missingPlatformAdminCapability('platform_admin_tenant_note_add');
      }
      throw error;
    }
    const note = mapTenantNote(asRecord(data));
    if (!note) throw new Error('The created note could not be read back.');
    return note;
  }

  async deleteTenantNote(noteId: string): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_tenant_note_delete',
      { p_note_id: noteId },
    );
    if (error) {
      if (isMissingRpc(error)) {
        throw missingPlatformAdminCapability(
          'platform_admin_tenant_note_delete',
        );
      }
      throw error;
    }
  }

  async saveTenantFollowUp(
    input: SavePlatformAdminTenantFollowUpInput,
  ): Promise<PlatformAdminTenantFollowUp> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_tenant_followup_save',
      {
        p_tenant_id: input.tenantId,
        p_stage: input.stage,
        p_next_action_at: input.nextActionAt ?? null,
        p_next_action_note: input.nextActionNote ?? null,
      },
    );
    if (error) {
      if (isMissingRpc(error)) {
        throw missingPlatformAdminCapability(
          'platform_admin_tenant_followup_save',
        );
      }
      throw error;
    }
    const followUp = mapTenantFollowUp(asRecord(data));
    if (!followUp) {
      throw new Error('The saved follow-up could not be read back.');
    }
    return followUp;
  }

  async listSubscriptionTenants(): Promise<PlatformAdminTenant[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_subscription_tenants',
      {},
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability(
          'platform_admin_subscription_tenants',
        );
      throw error;
    }
    return readArray(data).map((item) => mapTenant(asRecord(item)));
  }

  async listUsers(): Promise<PlatformAdminUser[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_users',
      {},
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_users');
      throw error;
    }
    return readArray(data).map((item) => mapPlatformUser(asRecord(item)));
  }

  async createTenantView(
    tenantId: string,
  ): Promise<PlatformAdminTenantViewHandoff> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_create_tenant_view',
      {
        p_tenant_id: tenantId,
      },
    );
    if (error) throw error;
    await this.recordAudit('impersonation.create', 'tenant', tenantId, null);
    return mapTenantViewHandoff(asRecord(data));
  }

  async consumeTenantView(
    token: string,
  ): Promise<PlatformAdminTenantViewAccess> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_consume_tenant_view',
      {
        p_token: token,
      },
    );
    if (error) throw error;
    return mapTenantViewAccess(asRecord(data));
  }

  async updateTenant(
    tenantId: string,
    patch: UpdatePlatformTenantInput,
  ): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_update_tenant',
      {
        p_tenant_id: tenantId,
        p_patch: patch,
      },
    );
    if (error) {
      if (isMissingRpc(error)) {
        throw new Error(
          'Tenant updates require the secured platform admin database migration.',
        );
      }
      throw error;
    }
    await this.recordAudit(
      'tenant.update',
      'tenant',
      tenantId,
      patch as Record<string, unknown>,
    );
  }

  async grantComplimentaryAccess(
    input: GrantComplimentaryAccessInput,
  ): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_grant_complimentary_access',
      {
        p_tenant_id: input.tenantId,
        p_plan: input.plan,
        p_source: input.source,
        p_starts_at: input.startsAt,
        p_ends_at: input.lifetime ? null : input.endsAt,
        p_lifetime: input.lifetime,
        p_reason: input.reason,
        p_note: input.note ?? null,
      },
    );
    if (error) throw error;
    await this.recordAudit(
      'subscription.access.grant',
      'tenant',
      input.tenantId,
      {
        plan: input.plan,
        source: input.source,
        startsAt: input.startsAt,
        endsAt: input.lifetime ? null : input.endsAt,
        lifetime: input.lifetime,
        reason: input.reason,
        hasNote: Boolean(input.note),
      },
    );
  }

  async revokeComplimentaryAccess(
    tenantId: string,
    note?: string,
  ): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_revoke_complimentary_access',
      { p_tenant_id: tenantId, p_note: note ?? null },
    );
    if (error) throw error;
    await this.recordAudit('subscription.access.revoke', 'tenant', tenantId, {
      hasNote: Boolean(note),
    });
  }

  async convertLegacyProToTrial(
    input: ConvertLegacyProToTrialInput,
  ): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_convert_legacy_pro_to_trial',
      {
        p_tenant_id: input.tenantId,
        p_trial_ends_at: input.trialEndsAt,
        p_reason: input.reason,
      },
    );
    if (error) throw error;
    await this.recordAudit(
      'subscription.access.convert_to_trial',
      'tenant',
      input.tenantId,
      { trialEndsAt: input.trialEndsAt, reason: input.reason },
    );
  }

  async getManualSubscriptionSettings(): Promise<PlatformManualSubscriptionSettings> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_get_manual_subscription_settings',
      {},
    );
    if (error) throw error;
    return mapManualSettings(asRecord(data));
  }

  async updateManualSubscriptionSettings(
    patch: UpdatePlatformManualSubscriptionSettings,
  ): Promise<PlatformManualSubscriptionSettings> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_update_manual_subscription_settings',
      { p_patch: patch },
    );
    if (error) throw error;
    await this.recordAudit(
      'subscription.instructions.update',
      'billing',
      null,
      {
        fields: Object.keys(patch).filter(
          (key) => patch[key as keyof typeof patch] !== null,
        ),
      },
    );
    return mapManualSettings(asRecord(data));
  }

  async listManualSubscriptionRequests(): Promise<
    PlatformManualSubscriptionRequest[]
  > {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_manual_subscription_requests',
      {},
    );
    if (error) throw error;
    return readArray(data).map((item) => mapManualRequest(asRecord(item)));
  }

  async reviewManualSubscriptionRequest(
    input: ReviewManualSubscriptionRequestInput,
  ): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_review_manual_subscription_request',
      {
        p_request_id: input.requestId,
        p_action: input.action,
        p_review_note: input.reviewNote ?? null,
        p_period_start: input.periodStart ?? null,
        p_period_end: input.periodEnd ?? null,
      },
    );
    if (error) throw error;
    await this.recordAudit(
      `subscription.request.${input.action}`,
      'subscription_request',
      input.requestId,
      {
        hasNote: Boolean(input.reviewNote),
      },
    );
  }

  async setModule(
    tenantId: string,
    moduleKey: string,
    isEnabled: boolean,
  ): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_set_module',
      {
        p_tenant_id: tenantId,
        p_module_key: moduleKey,
        p_is_enabled: isEnabled,
      },
    );
    if (error) {
      if (isMissingRpc(error)) {
        throw new Error(
          'Module updates require the secured platform admin database migration.',
        );
      }
      throw error;
    }
    await this.recordAudit('module.toggle', 'tenant', tenantId, {
      moduleKey,
      isEnabled,
    });
  }

  async deleteUser(userId: string): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_delete_user',
      {
        p_user_id: userId,
      },
    );
    if (error) {
      if (isMissingRpc(error)) {
        throw new Error(
          'User deletion requires the platform admin user deletion migration.',
        );
      }
      throw error;
    }
    await this.recordAudit('user.delete', 'user', userId, null);
  }

  async clearTenantData(tenantId: string): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_clear_tenant_data',
      {
        p_tenant_id: tenantId,
      },
    );
    if (error) {
      if (isMissingRpc(error)) {
        throw new Error(
          'Clearing tenant data requires the platform admin tenant lifecycle migration.',
        );
      }
      throw error;
    }
    await this.recordAudit('tenant.clear_data', 'tenant', tenantId, null);
  }

  async deleteTenant(tenantId: string): Promise<void> {
    const { data, error } = await this.supabase.functions.invoke(
      'platform-admin-delete-tenant',
      { body: { tenantId } },
    );
    if (error) {
      throw await readPlatformAdminFunctionError(
        error,
        'Deleting the tenant failed in the platform administration service.',
      );
    }

    const result = asRecord(data);
    if (result['ok'] !== true) {
      throw new Error(
        typeof result['error'] === 'string'
          ? result['error']
          : 'Deleting the tenant failed in the platform administration service.',
      );
    }

    if (result['cleanupStatus'] === 'pending') {
      console.warn(
        'Tenant data was deleted, but storage cleanup remains pending.',
        {
          tenantId,
          auditId: result['auditId'],
        },
      );
    }
  }

  async getSupabaseUsage(): Promise<PlatformAdminSupabaseUsage> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_supabase_usage',
      {},
    );
    if (error) {
      if (isMissingRpc(error)) {
        throw new Error(
          'Supabase usage requires the platform admin tenant lifecycle migration.',
        );
      }
      throw error;
    }
    return mapSupabaseUsage(asRecord(data));
  }

  async getAiOverview(): Promise<PlatformAdminAiOverview> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_ai_overview',
      {},
    );
    if (error) {
      if (isMissingRpc(error)) {
        throw new Error(
          'AI credits require the ai_credits migration (npx supabase db push).',
        );
      }
      throw error;
    }
    // The RPC returns JSONB already shaped in camelCase.
    return data as PlatformAdminAiOverview;
  }

  async getAiRequests(limit = 100): Promise<PlatformAdminAiRequest[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_ai_requests',
      { p_limit: limit },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_ai_requests');
      throw error;
    }
    return (data as PlatformAdminAiRequest[]) ?? [];
  }

  async topupTenantAiCredit(
    tenantId: string,
    credits: number,
    note?: string,
  ): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_ai_topup',
      { p_tenant_id: tenantId, p_credits: credits, p_note: note ?? null },
    );
    if (error) throw error;
    await this.recordAudit('ai.topup', 'tenant', tenantId, {
      credits,
      note: note ?? null,
    });
  }

  async adjustTenantAiCredit(
    tenantId: string,
    deltaCredits: number,
    note: string,
  ): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_ai_adjust_balance',
      {
        p_tenant_id: tenantId,
        p_delta_credits: deltaCredits,
        p_note: note,
      },
    );
    if (error) throw error;
    await this.recordAudit('ai.adjust', 'tenant', tenantId, {
      deltaCredits,
      note,
    });
  }

  async fulfillAiTopupRequest(requestId: string): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_ai_fulfill_request',
      { p_request_id: requestId },
    );
    if (error) throw error;
    await this.recordAudit('ai.fulfill', 'ai_topup', requestId, { requestId });
  }

  async cancelAiTopupRequest(requestId: string, note: string): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_ai_cancel_request',
      { p_request_id: requestId, p_note: note },
    );
    if (error) throw error;
    await this.recordAudit('ai.cancel', 'ai_topup', requestId, {
      requestId,
      note,
    });
  }

  async rejectAiTopupRequest(requestId: string, reason: string): Promise<void> {
    const normalizedReason = reason.trim();
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_ai_reject_request',
      { p_request_id: requestId, p_reason: normalizedReason },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability(
          'platform_admin_ai_reject_request',
        );
      throw error;
    }
    await this.recordAudit('ai.reject', 'ai_topup', requestId, {
      requestId,
      reason: normalizedReason,
    });
  }

  // ---- Monitoring (production observability) ----

  async getSystemOverview(): Promise<PlatformAdminSystemOverview> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_system_overview',
      {},
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_system_overview');
      throw error;
    }
    return data as PlatformAdminSystemOverview;
  }

  async getDeployments(limit = 100): Promise<PlatformAdminDeployments> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_deployments',
      { p_limit: limit },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_deployments');
      throw error;
    }
    return data as PlatformAdminDeployments;
  }

  async getLiveDeployments(): Promise<PlatformAdminLiveDeployments> {
    // Edge-function proxy to the GitHub Actions + Cloudflare Pages APIs.
    // A successful `{ available: false }` is an explicit unconfigured state.
    // Transport/invocation failures must remain errors so the UI can retry.
    const { data, error } =
      await this.supabase.functions.invoke('deployments-live');
    if (error) throw error;
    return (data as PlatformAdminLiveDeployments) ?? EMPTY_LIVE_DEPLOYMENTS;
  }

  async getDeploymentDetail(
    runId: string,
  ): Promise<PlatformAdminDeploymentDetail> {
    // Same edge-function proxy, called with a runId to fetch the run's live
    // jobs + steps from the GitHub Actions API. A successful unavailable
    // payload is distinct from an invocation/network failure.
    const { data, error } = await this.supabase.functions.invoke(
      'deployments-live',
      {
        body: { runId },
      },
    );
    if (error) throw error;
    return (data as PlatformAdminDeploymentDetail) ?? EMPTY_DEPLOYMENT_DETAIL;
  }

  async getUptime(
    range: MonitoringRange = '24h',
  ): Promise<PlatformAdminUptime> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_uptime',
      { p_range: range },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_uptime');
      throw error;
    }
    return data as PlatformAdminUptime;
  }

  async getErrors(
    range: MonitoringRange = '7d',
    source?: ErrorSource,
  ): Promise<PlatformAdminErrors> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_errors',
      { p_range: range, p_source: source ?? null },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_errors');
      throw error;
    }
    return data as PlatformAdminErrors;
  }

  async getErrorEvents(
    fingerprint: string,
    limit = 100,
  ): Promise<ErrorGroupDetail> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_error_events',
      { p_fingerprint: fingerprint, p_limit: limit },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_error_events');
      throw error;
    }
    return data as ErrorGroupDetail;
  }

  async getAuditLog(limit = 200, action?: string): Promise<PlatformAdminAudit> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_audit',
      {
        p_limit: limit,
        p_action: action ?? null,
        p_actor: null,
      },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_audit');
      throw error;
    }
    return data as PlatformAdminAudit;
  }

  async getEmailEvents(
    range: MonitoringRange = '7d',
  ): Promise<PlatformAdminEmailEvents> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_email_events',
      { p_range: range },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_email_events');
      throw error;
    }
    return data as PlatformAdminEmailEvents;
  }

  async getEdgeFunctions(
    range: MonitoringRange = '24h',
  ): Promise<PlatformAdminEdgeFunctions> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_edge_functions',
      { p_range: range },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_edge_functions');
      throw error;
    }
    return data as PlatformAdminEdgeFunctions;
  }

  async setErrorStatus(
    fingerprint: string,
    status: ErrorStatus,
  ): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_set_error_status',
      { p_fingerprint: fingerprint, p_status: status },
    );
    if (error) throw error;
  }

  // ---- Audience analytics + search console ----

  async getAudienceAnalytics(
    range: MonitoringRange = '7d',
  ): Promise<PlatformAdminAudienceAnalytics> {
    const client = this.supabase as unknown as RpcClient;
    const [audienceResult, locationsResult] = await Promise.all([
      client.rpc('platform_admin_audience_analytics', { p_range: range }),
      client.rpc('platform_admin_storefront_locations', { p_range: range }),
    ]);
    const { data, error } = audienceResult;
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability(
          'platform_admin_audience_analytics',
        );
      throw error;
    }
    if (locationsResult.error) {
      if (isMissingRpc(locationsResult.error))
        throw missingPlatformAdminCapability(
          'platform_admin_storefront_locations',
        );
      throw locationsResult.error;
    }

    const raw = asRecord(data);
    const cityRows: AudienceCityRevenueRow[] = readArray(locationsResult.data)
      .map((item) => {
        const row = asRecord(item);
        return {
          city: readString(row.city) ?? '—',
          orders: readNumber(row.orders),
          revenue: readNumber(row.revenue),
        };
      })
      .filter((row) => row.orders > 0 || row.revenue > 0);

    return {
      generatedAt: readString(raw.generatedAt) ?? '',
      range: readString(raw.range) ?? range,
      site: (raw.site ?? emptyAudienceSurface()) as AudienceSurface,
      storefront: (raw.storefront ?? emptyAudienceSurface()) as AudienceSurface,
      storefrontLocations: {
        cities: cityRows,
        governorates: rollupByGovernorate(
          cityRows.map((row) => ({
            city: row.city === '—' ? null : row.city,
            orders: row.orders,
            revenue: row.revenue,
          })),
        ),
      },
    };
  }

  async getSearchConsole(
    range: MonitoringRange = '7d',
  ): Promise<PlatformAdminSearchConsole> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_search_console',
      { p_range: range },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_search_console');
      throw error;
    }
    // The RPC returns JSON already shaped like PlatformAdminSearchConsole.
    return data as PlatformAdminSearchConsole;
  }

  async reportClientError(report: ClientErrorReport): Promise<void> {
    // Fire-and-forget: telemetry must never break the app. The fingerprint is
    // computed by the caller (the global error reporter, via @erp/domain/
    // monitoring) so this adapter stays free of domain deps — other apps that
    // bundle the adapter don't need the monitoring alias.
    try {
      if (!report.fingerprint) return;
      await (this.supabase as unknown as RpcClient).rpc('ingest_error_event', {
        p_fingerprint: report.fingerprint,
        p_surface: report.surface ?? 'dashboard',
        p_kind: report.kind ?? 'window_error',
        p_title: report.title ?? report.message,
        p_message: report.message,
        p_stack: report.stack ?? null,
        p_url: report.url ?? null,
        p_release: report.release ?? null,
        p_context: report.context ?? null,
      });
    } catch {
      /* swallow — never surface telemetry failures to the user */
    }
  }

  /**
   * Record a platform-admin action in the audit log. Best-effort: a failure
   * here must never break the action that was just performed. Tamper-resistant
   * because the underlying RPC asserts platform admin (a tenant user cannot
   * forge audit rows).
   */
  private async recordAudit(
    action: string,
    targetType: string | null,
    targetId: string | null,
    meta: Record<string, unknown> | null,
  ): Promise<void> {
    try {
      await (this.supabase as unknown as RpcClient).rpc(
        'platform_admin_audit_record',
        {
          p_action: action,
          p_target_type: targetType,
          p_target_id: targetId,
          p_target_label: null,
          p_meta: meta,
        },
      );
    } catch {
      /* audit is best-effort */
    }
  }

  private async isAdminFromExistingRpc(): Promise<boolean> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'is_admin',
      {},
    );
    if (error) throw error;
    return data === true;
  }

  private async withUsage(
    dashboard: PlatformAdminDashboard,
  ): Promise<PlatformAdminDashboard> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_usage',
      {},
    );
    if (error) {
      if (isMissingRpc(error)) return dashboard;
      throw error;
    }

    const usageByTenant = new Map(
      readArray(data).map((item) => {
        const usage = mapTenantUsage(asRecord(item));
        return [usage.tenantId, usage] as const;
      }),
    );
    const tenants = dashboard.tenants.map((tenant) => {
      const usage = usageByTenant.get(tenant.id);
      if (!usage) return tenant;
      return {
        ...tenant,
        storageBytes: usage.storageBytes,
        storageFiles: usage.storageFiles,
        storageBuckets: usage.storageBuckets,
        storefrontVisits: usage.storefrontVisits,
        storefrontUniqueVisitors: usage.storefrontUniqueVisitors,
        storefrontVisits30d: usage.storefrontVisits30d,
        storefrontUniqueVisitors30d: usage.storefrontUniqueVisitors30d,
      };
    });

    return {
      ...dashboard,
      tenants,
      totals: buildTotals(tenants),
    };
  }

  private async withSubscriptionAccess(
    dashboard: PlatformAdminDashboard,
  ): Promise<PlatformAdminDashboard> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_subscription_access',
      {},
    );
    if (error) {
      // Rolling deploy: keep the old dashboard usable until the migration lands.
      if (isMissingRpc(error) || isMissingAccessClassification(error))
        return dashboard;
      throw error;
    }

    const accessByTenant = new Map(
      readArray(data).map((item) => {
        const row = asRecord(item);
        return [readString(row.tenant_id) ?? '', row] as const;
      }),
    );
    const tenants = dashboard.tenants.map((tenant) => {
      const access = accessByTenant.get(tenant.id);
      if (!access) return tenant;
      return {
        ...tenant,
        subscriptionPlan: readString(access.plan),
        accessSource: readString(
          access.access_source,
        ) as PlatformAdminTenant['accessSource'],
        grantStartsAt: readString(access.grant_starts_at),
        grantEndsAt: readString(access.grant_ends_at),
        grantLifetime: readBoolean(access.grant_lifetime),
        grantReason: readString(access.grant_reason),
        grantNote: readString(access.grant_note),
        grantGrantedAt: readString(access.grant_granted_at),
        grantRevokedAt: readString(access.grant_revoked_at),
      };
    });
    return { ...dashboard, tenants, totals: buildTotals(tenants) };
  }

  private async withLandingUsage(
    dashboard: PlatformAdminDashboard,
  ): Promise<PlatformAdminDashboard> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_landing_usage',
      {},
    );
    if (error) {
      if (isMissingRpc(error)) return dashboard;
      throw error;
    }

    return {
      ...dashboard,
      landing: mapLandingUsage(asRecord(data)),
    };
  }

  // ---- Marketing blog (public landing site) ----

  async listBlogPosts(): Promise<BlogPost[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_blog_list',
      {},
    );
    if (error) throw error;
    return readArray(data).map((item) => mapBlogPost(asRecord(item)));
  }

  async upsertBlogPost(input: UpsertBlogPostInput): Promise<BlogPost> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_blog_upsert',
      {
        p_id: input.id ?? null,
        p_slug: input.slug,
        p_title: input.title,
        p_excerpt: input.excerpt ?? '',
        p_tag: input.tag ?? '',
        p_cover_image_url: input.coverImageUrl ?? null,
        p_content: input.content ?? '',
        p_is_published: input.isPublished ?? false,
        p_read_minutes: input.readMinutes ?? 3,
      },
    );
    if (error) throw error;
    await this.recordAudit(
      input.id ? 'blog.update' : 'blog.create',
      'blog_post',
      input.id ?? null,
      {
        title: input.title,
        slug: input.slug,
        published: input.isPublished ?? false,
      },
    );
    return mapBlogPost(asRecord(data));
  }

  async deleteBlogPost(id: string): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_blog_delete',
      { p_id: id },
    );
    if (error) throw error;
    await this.recordAudit('blog.delete', 'blog_post', id, null);
  }

  // ---- Beta-tester program ----

  async getBetaOverview(): Promise<PlatformAdminBetaOverview> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_beta_overview',
      {},
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_beta_overview');
      throw error;
    }
    return mapBetaOverview(asRecord(data));
  }

  async updateBetaConfig(
    input: UpdateBetaConfigInput,
  ): Promise<BetaProgramConfig> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_beta_config_update',
      {
        p_group_join_url: input.groupJoinUrl ?? null,
        p_play_optin_url: input.playOptinUrl ?? null,
        p_store_url: input.storeUrl ?? null,
        p_group_key: input.groupKey ?? null,
        p_is_live: input.isLive ?? null,
        p_note: input.note ?? null,
      },
    );
    if (error) throw error;
    await this.recordAudit('beta.config.update', 'beta_program', '1', null);
    return mapBetaConfig(asRecord(data));
  }

  // ---- Diffusions e-mail (console) ----

  async listBroadcastAudience(): Promise<BroadcastAudienceEntry[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_broadcast_audience',
      {},
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability(
          'platform_admin_broadcast_audience',
        );
      throw error;
    }
    return readArray(data).map((item) => mapBroadcastAudience(asRecord(item)));
  }

  async listBroadcasts(limit = 50): Promise<PlatformBroadcast[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_broadcast_history',
      { p_limit: limit },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability(
          'platform_admin_broadcast_history',
        );
      throw error;
    }
    return readArray(data).map((item) => mapBroadcast(asRecord(item)));
  }

  async sendBroadcastTest(input: SendBroadcastTestInput): Promise<void> {
    const { error } = await this.supabase.functions.invoke('admin-broadcast', {
      body: { test: true, testTo: input.to, message: input.message },
    });
    if (error) throw error;
    // Pas d'audit : un envoi de contrôle ne part que vers l'admin lui-même et
    // ne touche aucun client. L'audit est réservé aux envois réels.
  }

  async sendBroadcast(input: SendBroadcastInput): Promise<BroadcastSendResult> {
    const { data, error } = await this.supabase.functions.invoke(
      'admin-broadcast',
      {
        body: {
          segment: input.segment,
          message: input.message,
          recipients: input.recipients,
        },
      },
    );
    if (error) throw error;
    const payload = asRecord(data);
    const result: BroadcastSendResult = {
      broadcastId: readString(payload['broadcastId']) ?? '',
      sent: readNumber(payload['sent']),
      failed: readArray(payload['failed']).map((item) => {
        const row = asRecord(item);
        return {
          to: readString(row['to']) ?? '',
          error: readString(row['error']) ?? 'unknown',
        };
      }),
    };
    await this.recordAudit(
      'broadcast.send',
      'platform_broadcast',
      result.broadcastId || null,
      {
        segment: input.segment,
        recipients: input.recipients.length,
        sent: result.sent,
        failed: result.failed.length,
      },
    );
    return result;
  }

  // ---- Support chat / issue tracker (cross-tenant) ----

  async supportListThreads(
    filters?: PlatformAdminSupportFilters,
  ): Promise<PlatformAdminSupportThread[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_support_list_threads',
      {
        p_type: filters?.type ?? null,
        p_ticket_status: filters?.ticketStatus ?? null,
        p_priority: filters?.priority ?? null,
        p_search: filters?.search ?? null,
      },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability(
          'platform_admin_support_list_threads',
        );
      throw error;
    }
    return readArray(data).map((item) => mapSupportThread(asRecord(item)));
  }

  async supportGetMessages(
    threadId: string,
  ): Promise<PlatformAdminSupportMessage[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_support_get_messages',
      { p_thread_id: threadId },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability(
          'platform_admin_support_get_messages',
        );
      throw error;
    }
    return readArray(data).map((item) => mapSupportMessage(asRecord(item)));
  }

  async supportSendMessage(
    threadId: string,
    body: string,
  ): Promise<PlatformAdminSupportMessage> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_support_send_message',
      { p_thread_id: threadId, p_body: body },
    );
    if (error) throw error;
    return mapSupportMessage(asRecord(data));
  }

  async supportUpdateTicket(
    threadId: string,
    patch: PlatformAdminSupportTicketPatch,
  ): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_support_update_ticket',
      {
        p_thread_id: threadId,
        p_type: patch.type ?? null,
        p_title: patch.title ?? null,
        p_ticket_status: patch.ticketStatus ?? null,
        p_priority: patch.priority ?? null,
        p_tags: patch.tags ?? null,
      },
    );
    if (error) throw error;
  }

  async supportAddNote(
    threadId: string,
    body: string,
  ): Promise<PlatformAdminSupportNote> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_support_add_note',
      { p_thread_id: threadId, p_body: body },
    );
    if (error) throw error;
    return mapSupportNote(asRecord(data));
  }

  async supportListNotes(
    threadId: string,
  ): Promise<PlatformAdminSupportNote[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_support_list_notes',
      { p_thread_id: threadId },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability(
          'platform_admin_support_list_notes',
        );
      throw error;
    }
    return readArray(data).map((item) => mapSupportNote(asRecord(item)));
  }

  async supportCloseThread(threadId: string): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_support_close',
      { p_thread_id: threadId },
    );
    if (error) throw error;
  }

  // ---- App feedback (public tester inbox) ----

  async appFeedbackList(
    filters?: AppFeedbackFilters,
  ): Promise<AppFeedbackItem[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_app_feedback_list',
      {
        p_status: filters?.status ?? null,
        p_category: filters?.category ?? null,
        p_search: filters?.search ?? null,
      },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability(
          'platform_admin_app_feedback_list',
        );
      throw error;
    }
    return readArray(data).map((item) => mapAppFeedbackItem(asRecord(item)));
  }

  async appFeedbackSummary(): Promise<AppFeedbackSummary> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_app_feedback_summary',
      {},
    );
    if (error) {
      if (isMissingRpc(error)) {
        throw missingPlatformAdminCapability(
          'platform_admin_app_feedback_summary',
        );
      }
      throw error;
    }
    const row = asRecord(data);
    return {
      total: readNumber(row.total),
      new: readNumber(row.new),
      triaged: readNumber(row.triaged),
      resolved: readNumber(row.resolved),
      archived: readNumber(row.archived),
      avgRating: row.avgRating != null ? readNumber(row.avgRating) : null,
    };
  }

  async appFeedbackUpdate(id: string, patch: AppFeedbackPatch): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_app_feedback_update',
      {
        p_id: id,
        p_status: patch.status ?? null,
        p_admin_note: patch.adminNote ?? null,
      },
    );
    if (error) throw error;
  }

  async carrierRequestsList(
    filters?: AdminCarrierRequestFilters,
  ): Promise<AdminCarrierRequest[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_carrier_requests_list',
      { p_status: filters?.status ?? null, p_search: filters?.search ?? null },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability(
          'platform_admin_carrier_requests_list',
        );
      throw error;
    }
    return readArray(data).map((x) => mapAdminCarrierRequest(asRecord(x)));
  }
  async carrierRequestProviders(
    requestId: string,
  ): Promise<AdminCarrierRequestProvider[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_carrier_request_providers',
      { p_request_id: requestId },
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability(
          'platform_admin_carrier_request_providers',
        );
      throw error;
    }
    return readArray(data).map((x) => {
      const r = asRecord(x);
      return {
        id: readString(r.id) ?? '',
        code: readString(r.code) ?? '',
        displayName: readString(r.displayName) ?? '',
        isEnabled: readBoolean(r.isEnabled),
        isConfigured: readBoolean(r.isConfigured),
      };
    });
  }
  async carrierRequestUpdate(
    id: string,
    patch: AdminCarrierRequestPatch,
  ): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_carrier_request_update',
      {
        p_id: id,
        p_status: patch.status ?? null,
        p_merchant_message: patch.merchantMessage ?? null,
        p_internal_note: patch.internalNote ?? null,
        p_linked_provider_id: patch.linkedProviderId ?? null,
      },
    );
    if (error) throw error;
  }
  async carrierRequestSetupManual(id: string): Promise<string> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_carrier_request_setup_manual',
      { p_id: id },
    );
    if (error) throw error;
    return readString(data) ?? '';
  }

  // ---- Team & RBAC ----------------------------------------------------------

  async getMySections(): Promise<PlatformAdminSections> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_my_sections',
      {},
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_my_sections');
      throw error;
    }
    const row = asRecord(data);
    return {
      full: row['full'] === true,
      sections: readArray(row['sections']).map((s) => String(s)),
    };
  }

  async teamList(): Promise<PlatformAdminTeamMember[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_team_list',
      {},
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_team_list');
      throw error;
    }
    return readArray(data).map((item) => {
      const r = asRecord(item);
      return {
        userId: String(r['userId']),
        email: String(r['email'] ?? ''),
        roleId: (r['roleId'] as string | null) ?? null,
        roleName: (r['roleName'] as string | null) ?? null,
        isFull: r['isFull'] === true,
        isSelf: r['isSelf'] === true,
        addedAt: String(r['addedAt'] ?? ''),
        lastSignInAt: (r['lastSignInAt'] as string | null) ?? null,
      };
    });
  }

  async teamAdd(
    email: string,
    roleId: string | null,
  ): Promise<PlatformAdminTeamAddResult> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_team_add',
      { p_email: email, p_role_id: roleId },
    );
    if (error) throw error;
    const r = asRecord(data);
    return {
      ok: r['ok'] === true,
      reason: r['reason'] === 'no_user' ? 'no_user' : undefined,
    };
  }

  async teamInvite(
    email: string,
    roleId: string | null,
  ): Promise<PlatformAdminTeamInviteResult> {
    // Préfère l'Edge Function send-admin-invite : elle enregistre via la RPC
    // ET envoie un vrai email d'invitation. Repli sur la RPC seule (enregistre
    // sans email) si la fonction n'est pas déployée / échoue côté transport.
    try {
      const { data, error } = await this.supabase.functions.invoke(
        'send-admin-invite',
        {
          body: { email, roleId },
        },
      );
      if (error) throw error;
      const r = asRecord(data);
      return {
        status: r['status'] === 'added' ? 'added' : 'invited',
        emailSent: r['emailSent'] === true,
      };
    } catch {
      const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
        'platform_admin_team_invite',
        { p_email: email, p_role_id: roleId },
      );
      if (error) throw error;
      const r = asRecord(data);
      return {
        status: r['status'] === 'added' ? 'added' : 'invited',
        emailSent: false,
      };
    }
  }

  async invitationsList(): Promise<PlatformAdminInvitation[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_invitations_list',
      {},
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_invitations_list');
      throw error;
    }
    return readArray(data).map((item) => {
      const r = asRecord(item);
      return {
        id: String(r['id']),
        email: String(r['email'] ?? ''),
        roleId: (r['roleId'] as string | null) ?? null,
        roleName: (r['roleName'] as string | null) ?? null,
        createdAt: String(r['createdAt'] ?? ''),
      };
    });
  }

  async invitationCancel(id: string): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_invitation_cancel',
      { p_id: id },
    );
    if (error) throw error;
  }

  async claimAdminInvitation(): Promise<boolean> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_claim_invitation',
      {},
    );
    // Tolérant : RPC absente (avant migration) → rien à réclamer.
    if (error) {
      if (isMissingRpc(error)) return false;
      throw error;
    }
    return data === true;
  }

  async teamSetRole(userId: string, roleId: string | null): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_team_set_role',
      { p_user_id: userId, p_role_id: roleId },
    );
    if (error) throw error;
  }

  async teamRemove(userId: string): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_team_remove',
      { p_user_id: userId },
    );
    if (error) throw error;
  }

  async roleList(): Promise<PlatformAdminRole[]> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_role_list',
      {},
    );
    if (error) {
      if (isMissingRpc(error))
        throw missingPlatformAdminCapability('platform_admin_role_list');
      throw error;
    }
    return readArray(data).map((item) => mapAdminRole(asRecord(item)));
  }

  async roleUpsert(
    input: UpsertPlatformAdminRoleInput,
  ): Promise<PlatformAdminRole> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_role_upsert',
      {
        p_id: input.id ?? null,
        p_name: input.name,
        p_sections: input.sections,
      },
    );
    if (error) throw error;
    return mapAdminRole(asRecord(data));
  }

  async roleDelete(id: string): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_role_delete',
      { p_id: id },
    );
    if (error) throw error;
  }
}

function mapOnboardingProfile(
  row: Record<string, unknown>,
): PlatformAdminOnboardingProfile | null {
  const primaryGoals = readArray(row['primaryGoals']).map(String);
  const legacyPrimaryGoal = readString(row['primaryGoal']);
  const parsed = storedOnboardingProfileSchema.safeParse({
    questionnaireVersion: Number(row['questionnaireVersion']),
    managementMode: String(row['managementMode'] ?? ''),
    previousSoftwareName: readString(row['previousSoftwareName']),
    migrationIntent: readString(row['migrationIntent']),
    primaryGoals:
      primaryGoals.length > 0
        ? primaryGoals
        : legacyPrimaryGoal
          ? [legacyPrimaryGoal]
          : [],
    activitySector: readString(row['activitySector']),
    salesChannels: readArray(row['salesChannels']).map(String),
    paymentModes: readArray(row['paymentModes']).map(String),
    onlineSalesChannels: readArray(row['onlineSalesChannels']).map(String),
    websiteUrl: readString(row['websiteUrl']),
    ecommercePlatform: readString(row['ecommercePlatform']),
    ecommercePlatformName: readString(row['ecommercePlatformName']),
    acquisitionSource: readString(row['acquisitionSource']),
    acquisitionSourceDetail: readString(row['acquisitionSourceDetail']),
    assistanceRequested: row['assistanceRequested'] === true,
    contactConsent: row['contactConsent'] === true,
    tenantId: String(row['tenantId'] ?? ''),
    createdBy: readString(row['createdBy']),
    capturedAt: String(row['capturedAt'] ?? ''),
  });
  // A damaged row must not take down the complete admin console. Future
  // questionnaire codes remain supported by the tolerant stored schema; only
  // structurally unsafe rows (invalid tenant id/URL/timestamp shape) are skipped.
  return parsed.success ? parsed.data : null;
}

function mapAdminRole(row: Record<string, unknown>): PlatformAdminRole {
  return {
    id: String(row['id']),
    name: String(row['name'] ?? ''),
    sections: readArray(row['sections']).map((s) => String(s)),
    memberCount: Number(row['memberCount'] ?? 0),
    createdAt: String(row['createdAt'] ?? ''),
    updatedAt: String(row['updatedAt'] ?? ''),
  };
}

function isMissingRpc(error: SupabaseError): boolean {
  return (
    error.code === 'PGRST202' ||
    /could not find the function/i.test(error.message)
  );
}

function missingPlatformAdminCapability(rpcName: string): Error {
  return new Error(
    `The secured platform-admin database capability ${rpcName} is not deployed.`,
  );
}

function isMissingAccessClassification(error: SupabaseError): boolean {
  return (
    error.code === '42703' ||
    error.code === 'PGRST204' ||
    /access_source|grant_starts_at/i.test(error.message)
  );
}

function buildTotals(tenants: PlatformAdminTenant[]): PlatformAdminTotals {
  return tenants.reduce<PlatformAdminTotals>(
    (totals, tenant) => ({
      tenants: totals.tenants + 1,
      activeTenants: totals.activeTenants + (tenant.isActive ? 1 : 0),
      suspendedTenants: totals.suspendedTenants + (tenant.isActive ? 0 : 1),
      trialingTenants:
        totals.trialingTenants +
        (tenant.subscriptionStatus === 'trialing' ? 1 : 0),
      paidTenants:
        totals.paidTenants +
        (tenant.subscriptionStatus === 'active' &&
        (tenant.accessSource === 'paid' || tenant.accessSource == null)
          ? 1
          : 0),
      pastDueTenants:
        totals.pastDueTenants +
        (tenant.subscriptionStatus === 'past_due' ? 1 : 0),
      trialEndingSoon:
        totals.trialEndingSoon +
        (isTrialEndingSoon(tenant.trialEndsAt) ? 1 : 0),
      teamMembers: totals.teamMembers + tenant.teamMembers,
      clients: totals.clients + tenant.clients,
      articles: totals.articles + tenant.articles,
      salesDocuments: totals.salesDocuments + tenant.salesDocuments,
      invoiceRevenue: totals.invoiceRevenue + tenant.invoiceRevenue,
      onlineRevenue: totals.onlineRevenue + tenant.onlineRevenue,
      onlineOrders: totals.onlineOrders + tenant.onlineOrders,
      onlineCustomers: totals.onlineCustomers + tenant.onlineCustomers,
      publishedProducts: totals.publishedProducts + tenant.publishedProducts,
      openAbandonedCarts: totals.openAbandonedCarts + tenant.openAbandonedCarts,
      abandonedCartValue: totals.abandonedCartValue + tenant.abandonedCartValue,
      proCustomers: totals.proCustomers + tenant.proCustomers,
      pendingProCustomers:
        totals.pendingProCustomers + tenant.pendingProCustomers,
      proOrders: totals.proOrders + tenant.proOrders,
      proOrderTotal: totals.proOrderTotal + tenant.proOrderTotal,
      storageBytes: totals.storageBytes + tenant.storageBytes,
      storageFiles: totals.storageFiles + tenant.storageFiles,
      storefrontVisits: totals.storefrontVisits + tenant.storefrontVisits,
      storefrontUniqueVisitors:
        totals.storefrontUniqueVisitors + tenant.storefrontUniqueVisitors,
      storefrontVisits30d:
        totals.storefrontVisits30d + tenant.storefrontVisits30d,
      storefrontUniqueVisitors30d:
        totals.storefrontUniqueVisitors30d + tenant.storefrontUniqueVisitors30d,
      unreadNotifications:
        totals.unreadNotifications + tenant.unreadNotifications,
      revenue30d: totals.revenue30d + tenant.revenue30d,
    }),
    {
      tenants: 0,
      activeTenants: 0,
      suspendedTenants: 0,
      trialingTenants: 0,
      paidTenants: 0,
      pastDueTenants: 0,
      trialEndingSoon: 0,
      teamMembers: 0,
      clients: 0,
      articles: 0,
      salesDocuments: 0,
      invoiceRevenue: 0,
      onlineRevenue: 0,
      onlineOrders: 0,
      onlineCustomers: 0,
      publishedProducts: 0,
      openAbandonedCarts: 0,
      abandonedCartValue: 0,
      proCustomers: 0,
      pendingProCustomers: 0,
      proOrders: 0,
      proOrderTotal: 0,
      storageBytes: 0,
      storageFiles: 0,
      storefrontVisits: 0,
      storefrontUniqueVisitors: 0,
      storefrontVisits30d: 0,
      storefrontUniqueVisitors30d: 0,
      unreadNotifications: 0,
      revenue30d: 0,
    },
  );
}

function isTrialEndingSoon(value: string | null): boolean {
  if (!value) return false;
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return false;
  const now = Date.now();
  return time >= now && time <= now + 7 * 24 * 60 * 60 * 1000;
}

function mapManualSettings(
  row: Record<string, unknown>,
): PlatformManualSubscriptionSettings {
  return {
    d17RecipientName: readString(row.d17RecipientName),
    d17RecipientIdentifier: readString(row.d17RecipientIdentifier),
    d17Instructions: readString(row.d17Instructions),
    bankBeneficiary: readString(row.bankBeneficiary),
    bankName: readString(row.bankName),
    bankRib: readString(row.bankRib),
    bankIban: readString(row.bankIban),
    bankBic: readString(row.bankBic),
    bankInstructions: readString(row.bankInstructions),
    updatedAt: readString(row.updatedAt),
  };
}

function mapManualRequest(
  row: Record<string, unknown>,
): PlatformManualSubscriptionRequest {
  const cycle = readString(row.billingCycle);
  const plan = readString(row.plan);

  const method = readString(row.paymentMethod);
  const status = readString(row.status);
  return {
    id: readString(row.id) ?? '',
    tenantId: readString(row.tenantId) ?? '',
    tenantName: readString(row.tenantName) ?? '',
    requesterEmail: readString(row.requesterEmail),
    // Read the tier the customer actually requested. Hardcoding it here used
    // to be harmless (one plan existed); with tiers it would show staff the
    // wrong plan and the wrong expected amount.
    plan: PLAN_CODES.includes(plan as PlanCode)
      ? (plan as PlanCode)
      : 'commerce',
    billingCycle: BILLING_CYCLE_VALUES.includes(cycle as BillingCycle)
      ? (cycle as BillingCycle)
      : 'yearly',
    amountMillimes: readNumber(row.amountMillimes),
    paymentMethod: method === 'd17_poste' ? 'd17_poste' : 'bank_transfer',
    proofPath: readString(row.proofPath) ?? '',
    customerNote: readString(row.customerNote),
    status:
      status === 'correction_requested' ||
      status === 'approved' ||
      status === 'rejected' ||
      status === 'canceled'
        ? status
        : 'pending',
    reviewNote: readString(row.reviewNote),
    createdAt: readString(row.createdAt) ?? '',
    updatedAt: readString(row.updatedAt) ?? '',
    reviewedAt: readString(row.reviewedAt),
    activatedAt: readString(row.activatedAt),
    trialEndsAt: readString(row.trialEndsAt),
    currentPeriodEnd: readString(row.currentPeriodEnd),
  };
}

function mapDashboard(
  row: Record<string, unknown>,
  adminRpc = false,
): PlatformAdminDashboard {
  const features = asRecord(row.features);
  return {
    generatedAt: readString(row.generatedAt) ?? new Date().toISOString(),
    features: {
      adminRpc: readNullableBoolean(features.adminRpc) ?? adminRpc,
      subscriptionStatusWrite:
        readNullableBoolean(features.subscriptionStatusWrite) ?? adminRpc,
    },
    totals: mapTotals(asRecord(row.totals)),
    landing: mapLandingUsage(asRecord(row.landing)),
    tenants: readArray(row.tenants).map((item) => mapTenant(asRecord(item))),
    monthlyRevenue: readArray(row.monthlyRevenue).map((item) =>
      mapMonthlyRevenue(asRecord(item)),
    ),
  };
}

function mapTenantViewHandoff(
  row: Record<string, unknown>,
): PlatformAdminTenantViewHandoff {
  return {
    token: readString(row.token) ?? '',
    ...mapTenantViewAccess(row),
  };
}

function mapTenantViewAccess(
  row: Record<string, unknown>,
): PlatformAdminTenantViewAccess {
  return {
    tenantId: readString(row.tenantId) ?? '',
    expiresAt: readString(row.expiresAt) ?? '',
    viewExpiresAt: readString(row.viewExpiresAt) ?? '',
  };
}

function mapTotals(row: Record<string, unknown>): PlatformAdminTotals {
  return {
    tenants: readNumber(row.tenants),
    activeTenants: readNumber(row.activeTenants),
    suspendedTenants: readNumber(row.suspendedTenants),
    trialingTenants: readNumber(row.trialingTenants),
    paidTenants: readNumber(row.paidTenants),
    pastDueTenants: readNumber(row.pastDueTenants),
    trialEndingSoon: readNumber(row.trialEndingSoon),
    teamMembers: readNumber(row.teamMembers),
    clients: readNumber(row.clients),
    articles: readNumber(row.articles),
    salesDocuments: readNumber(row.salesDocuments),
    invoiceRevenue: readNumber(row.invoiceRevenue),
    onlineRevenue: readNumber(row.onlineRevenue),
    onlineOrders: readNumber(row.onlineOrders),
    onlineCustomers: readNumber(row.onlineCustomers),
    publishedProducts: readNumber(row.publishedProducts),
    openAbandonedCarts: readNumber(row.openAbandonedCarts),
    abandonedCartValue: readNumber(row.abandonedCartValue),
    proCustomers: readNumber(row.proCustomers),
    pendingProCustomers: readNumber(row.pendingProCustomers),
    proOrders: readNumber(row.proOrders),
    proOrderTotal: readNumber(row.proOrderTotal),
    storageBytes: readNumber(row.storageBytes),
    storageFiles: readNumber(row.storageFiles),
    storefrontVisits: readNumber(row.storefrontVisits),
    storefrontUniqueVisitors: readNumber(row.storefrontUniqueVisitors),
    storefrontVisits30d: readNumber(row.storefrontVisits30d),
    storefrontUniqueVisitors30d: readNumber(row.storefrontUniqueVisitors30d),
    unreadNotifications: readNumber(row.unreadNotifications),
    revenue30d: readNumber(row.revenue30d),
  };
}

function mapTenantUsage(
  row: Record<string, unknown>,
): Pick<
  PlatformAdminTenant,
  | 'id'
  | 'storageBytes'
  | 'storageFiles'
  | 'storageBuckets'
  | 'storefrontVisits'
  | 'storefrontUniqueVisitors'
  | 'storefrontVisits30d'
  | 'storefrontUniqueVisitors30d'
> & { tenantId: string } {
  return {
    id: readString(row.tenantId) ?? '',
    tenantId: readString(row.tenantId) ?? '',
    storageBytes: readNumber(row.storageBytes),
    storageFiles: readNumber(row.storageFiles),
    storageBuckets: readArray(row.storageBuckets)
      .map((item) => {
        const bucket = asRecord(item);
        return {
          bucket: readString(bucket.bucket) ?? '',
          files: readNumber(bucket.files),
          bytes: readNumber(bucket.bytes),
        };
      })
      .filter((bucket) => bucket.bucket),
    storefrontVisits: readNumber(row.storefrontVisits),
    storefrontUniqueVisitors: readNumber(row.storefrontUniqueVisitors),
    storefrontVisits30d: readNumber(row.storefrontVisits30d),
    storefrontUniqueVisitors30d: readNumber(row.storefrontUniqueVisitors30d),
  };
}

function mapSupabaseUsage(
  row: Record<string, unknown>,
): PlatformAdminSupabaseUsage {
  const database = asRecord(row.database);
  const storage = asRecord(row.storage);
  const auth = asRecord(row.auth);
  const tenants = asRecord(row.tenants);
  const limits = asRecord(row.limits);
  return {
    generatedAt: readString(row.generatedAt) ?? new Date().toISOString(),
    database: {
      sizeBytes: readNumber(database.sizeBytes),
      tableCount: readNumber(database.tableCount),
      indexesBytes: readNumber(database.indexesBytes),
      connections: readNumber(database.connections),
    },
    tables: readArray(row.tables)
      .map((item) => {
        const table = asRecord(item);
        return {
          name: readString(table.name) ?? '',
          sizeBytes: readNumber(table.sizeBytes),
          rowEstimate: readNumber(table.rowEstimate),
        };
      })
      .filter((table) => table.name),
    storage: {
      totalBytes: readNumber(storage.totalBytes),
      totalFiles: readNumber(storage.totalFiles),
      buckets: readArray(storage.buckets)
        .map((item) => {
          const bucket = asRecord(item);
          return {
            bucket: readString(bucket.bucket) ?? '',
            files: readNumber(bucket.files),
            bytes: readNumber(bucket.bytes),
          };
        })
        .filter((bucket) => bucket.bucket),
      perTenant: readArray(storage.perTenant)
        .map((item) => {
          const tenant = asRecord(item);
          return {
            tenantId: readString(tenant.tenantId) ?? '',
            name: readString(tenant.name) ?? '',
            bytes: readNumber(tenant.bytes),
            files: readNumber(tenant.files),
          };
        })
        .filter((tenant) => tenant.tenantId),
    },
    auth: {
      users: readNumber(auth.users),
      confirmedUsers: readNumber(auth.confirmedUsers),
    },
    tenants: {
      total: readNumber(tenants.total),
      active: readNumber(tenants.active),
    },
    limits: {
      databaseBytes: readNumber(limits.databaseBytes),
      storageBytes: readNumber(limits.storageBytes),
      monthlyActiveUsers: readNumber(limits.monthlyActiveUsers),
    },
  };
}

function mapLandingUsage(
  row: Record<string, unknown>,
): PlatformAdminLandingUsage {
  return {
    visits: readNumber(row.visits),
    uniqueVisitors: readNumber(row.uniqueVisitors),
    visits30d: readNumber(row.visits30d),
    uniqueVisitors30d: readNumber(row.uniqueVisitors30d),
    topPaths30d: readArray(row.topPaths30d)
      .map((item) => {
        const path = asRecord(item);
        return {
          path: readString(path.path) ?? '/',
          visits: readNumber(path.visits),
          uniqueVisitors: readNumber(path.uniqueVisitors),
        };
      })
      .filter((item) => item.path),
  };
}

function mapTenant(row: Record<string, unknown>): PlatformAdminTenant {
  return {
    id: readString(row.id) ?? '',
    name: readString(row.name) ?? '',
    subdomain: readString(row.subdomain) ?? '',
    customDomain: readString(row.customDomain),
    mf: readString(row.mf),
    phone: readString(row.phone),
    email: readString(row.email),
    ownerUserId: readString(row.ownerUserId) ?? '',
    ownerEmail: readString(row.ownerEmail),
    plan: readString(row.plan) ?? 'free',
    isActive: readBoolean(row.isActive),
    createdAt: readString(row.createdAt) ?? '',
    updatedAt: readString(row.updatedAt) ?? '',
    companyName: readString(row.companyName),
    storeName: readString(row.storeName),
    logoUrl: readString(row.logoUrl),
    primaryColor: readString(row.primaryColor),
    defaultLanguage: readString(row.defaultLanguage),
    currency: readString(row.currency),
    whatsappEnabled: readNullableBoolean(row.whatsappEnabled),
    storeCustomDomain: readString(row.storeCustomDomain),
    subscriptionStatus: readString(
      row.subscriptionStatus,
    ) as PlatformAdminTenant['subscriptionStatus'],
    subscriptionPlan: readString(row.subscriptionPlan),
    billingCycle: readString(row.billingCycle),
    trialEndsAt: readString(row.trialEndsAt),
    currentPeriodEnd: readString(row.currentPeriodEnd),
    canceledAt: readString(row.canceledAt),
    accessSource: readString(
      row.accessSource,
    ) as PlatformAdminTenant['accessSource'],
    grantStartsAt: readString(row.grantStartsAt),
    grantEndsAt: readString(row.grantEndsAt),
    grantLifetime: readBoolean(row.grantLifetime),
    grantReason: readString(row.grantReason),
    grantNote: readString(row.grantNote),
    grantGrantedAt: readString(row.grantGrantedAt),
    grantRevokedAt: readString(row.grantRevokedAt),
    teamMembers: readNumber(row.teamMembers),
    enabledModules: readNumber(row.enabledModules),
    modules: readArray(row.modules).map((item) => mapModule(asRecord(item))),
    salesDocuments: readNumber(row.salesDocuments),
    salesOrders: readNumber(row.salesOrders),
    invoices: readNumber(row.invoices),
    invoiceRevenue: readNumber(row.invoiceRevenue),
    onlineOrders: readNumber(row.onlineOrders),
    onlineRevenue: readNumber(row.onlineRevenue),
    revenue30d: readNumber(row.revenue30d),
    purchaseDocuments: readNumber(row.purchaseDocuments),
    purchaseInvoiceTotal: readNumber(row.purchaseInvoiceTotal),
    clients: readNumber(row.clients),
    suppliers: readNumber(row.suppliers),
    articles: readNumber(row.articles),
    payments: readNumber(row.payments),
    paymentTotal: readNumber(row.paymentTotal),
    storefrontProducts: readNumber(row.storefrontProducts),
    publishedProducts: readNumber(row.publishedProducts),
    onlineCustomers: readNumber(row.onlineCustomers),
    onlineCustomerLtv: readNumber(row.onlineCustomerLtv),
    openAbandonedCarts: readNumber(row.openAbandonedCarts),
    abandonedCartValue: readNumber(row.abandonedCartValue),
    proCustomers: readNumber(row.proCustomers),
    pendingProCustomers: readNumber(row.pendingProCustomers),
    approvedProCustomers: readNumber(row.approvedProCustomers),
    proOrders: readNumber(row.proOrders),
    proOrderTotal: readNumber(row.proOrderTotal),
    storageBytes: readNumber(row.storageBytes),
    storageFiles: readNumber(row.storageFiles),
    storageBuckets: readArray(row.storageBuckets)
      .map((item) => {
        const bucket = asRecord(item);
        return {
          bucket: readString(bucket.bucket) ?? '',
          files: readNumber(bucket.files),
          bytes: readNumber(bucket.bytes),
        };
      })
      .filter((bucket) => bucket.bucket),
    storefrontVisits: readNumber(row.storefrontVisits),
    storefrontUniqueVisitors: readNumber(row.storefrontUniqueVisitors),
    storefrontVisits30d: readNumber(row.storefrontVisits30d),
    storefrontUniqueVisitors30d: readNumber(row.storefrontUniqueVisitors30d),
    unreadNotifications: readNumber(row.unreadNotifications),
    lastActivityAt: readString(row.lastActivityAt),
  };
}

function mapPlatformUser(row: Record<string, unknown>): PlatformAdminUser {
  return {
    userId: readString(row.userId) ?? '',
    email: readString(row.email) ?? '',
    tenantId: readString(row.tenantId) ?? '',
    tenantName: readString(row.tenantName) ?? '',
    tenantSubdomain: readString(row.tenantSubdomain) ?? '',
    roleSlug: readString(row.roleSlug),
    roleName: readString(row.roleName),
    isOwner: readBoolean(row.isOwner),
    isPlatformAdmin: readBoolean(row.isPlatformAdmin),
    isProCustomer: readBoolean(row.isProCustomer),
    createdAt: readString(row.createdAt) ?? '',
    lastSignInAt: readString(row.lastSignInAt),
    emailConfirmedAt: readString(row.emailConfirmedAt),
  };
}

function mapModule(row: Record<string, unknown>): PlatformAdminModuleState {
  return {
    key: readString(row.key) ?? '',
    enabled: readBoolean(row.enabled),
  };
}

function mapMonthlyRevenue(
  row: Record<string, unknown>,
): PlatformAdminMonthlyRevenue {
  return {
    month: readString(row.month) ?? '',
    invoiceRevenue: readNumber(row.invoiceRevenue),
    onlineRevenue: readNumber(row.onlineRevenue),
    onlineOrders: readNumber(row.onlineOrders),
  };
}

function mapBlogPost(row: Record<string, unknown>): BlogPost {
  return {
    id: readString(row.id) ?? '',
    slug: readString(row.slug) ?? '',
    title: readString(row.title) ?? '',
    excerpt: readString(row.excerpt) ?? '',
    tag: readString(row.tag) ?? '',
    coverImageUrl: readString(row.cover_image_url),
    content: readString(row.content) ?? '',
    isPublished: readBoolean(row.is_published),
    publishedAt: readString(row.published_at),
    readMinutes: readNumber(row.read_minutes),
    createdAt: readString(row.created_at) ?? '',
    updatedAt: readString(row.updated_at) ?? '',
  };
}

function mapBetaConfig(row: Record<string, unknown>): BetaProgramConfig {
  return {
    groupJoinUrl: readString(row.groupJoinUrl),
    playOptinUrl: readString(row.playOptinUrl),
    storeUrl: readString(row.storeUrl),
    groupKey: readString(row.groupKey),
    isLive: readBoolean(row.isLive),
    note: readString(row.note),
    updatedAt: readString(row.updatedAt),
  };
}

function mapBetaSignup(row: Record<string, unknown>): PlatformAdminBetaSignup {
  return {
    id: readString(row.id) ?? '',
    email: readString(row.email) ?? '',
    name: readString(row.name),
    lang: readString(row.lang) ?? 'fr',
    source: readString(row.source) ?? 'landing',
    status: readString(row.status) ?? 'pending',
    groupAddedAt: readString(row.groupAddedAt),
    createdAt: readString(row.createdAt) ?? '',
    groupClickedAt: readString(row.groupClickedAt),
    optinClickedAt: readString(row.optinClickedAt),
    appFirstOpenedAt: readString(row.appFirstOpenedAt),
    appLastSeenAt: readString(row.appLastSeenAt),
    appOpenCount: readNumber(row.appOpenCount),
    appPlatform: readString(row.appPlatform),
    appVersion: readString(row.appVersion),
    stage: (readString(row.stage) ??
      'pending') as PlatformAdminBetaSignup['stage'],
  };
}

function mapBetaOverview(
  row: Record<string, unknown>,
): PlatformAdminBetaOverview {
  const stats = asRecord(row.stats);
  return {
    generatedAt: readString(row.generatedAt) ?? '',
    config: mapBetaConfig(asRecord(row.config)),
    stats: {
      total: readNumber(stats.total),
      groupClicked: readNumber(stats.groupClicked),
      optinClicked: readNumber(stats.optinClicked),
      appOpened: readNumber(stats.appOpened),
      active7d: readNumber(stats.active7d),
      last7d: readNumber(stats.last7d),
      lastSignupAt: readString(stats.lastSignupAt),
      optedIn: readNumber(stats.optedIn),
      groupAdded: readNumber(stats.groupAdded),
    },
    signups: readArray(row.signups).map((item) =>
      mapBetaSignup(asRecord(item)),
    ),
  };
}

function mapBroadcastAudience(
  row: Record<string, unknown>,
): BroadcastAudienceEntry {
  return {
    email: readString(row.email) ?? '',
    tenantId: readString(row.tenantId),
    tenantName: readString(row.tenantName),
    subscriptionStatus: readString(row.subscriptionStatus),
    trialEndsAt: readString(row.trialEndsAt),
    emailConfirmed: readBoolean(row.emailConfirmed),
    isPlatformAdmin: readBoolean(row.isPlatformAdmin),
    isProCustomer: readBoolean(row.isProCustomer),
    locale: readString(row.locale),
  };
}

function mapBroadcast(row: Record<string, unknown>): PlatformBroadcast {
  return {
    id: readString(row.id) ?? '',
    subject: readString(row.subject) ?? '',
    body: readString(row.body) ?? '',
    segment: readString(row.segment) ?? '',
    recipientsCount: readNumber(row.recipientsCount),
    sentCount: readNumber(row.sentCount),
    failedCount: readNumber(row.failedCount),
    status: (readString(row.status) ?? 'sent') as PlatformBroadcast['status'],
    recipients: readArray(row.recipients)
      .map((item) => (typeof item === 'string' ? item : ''))
      .filter(Boolean),
    createdByEmail: readString(row.createdByEmail),
    createdAt: readString(row.createdAt) ?? '',
    sentAt: readString(row.sentAt),
  };
}

function mapSupportThread(
  row: Record<string, unknown>,
): PlatformAdminSupportThread {
  return {
    id: readString(row.id) ?? '',
    tenantId: readString(row.tenantId) ?? '',
    tenantName: readString(row.tenantName),
    status: (readString(row.status) ??
      'ai') as PlatformAdminSupportThread['status'],
    type: (readString(row.type) ??
      'support') as PlatformAdminSupportThread['type'],
    title: readString(row.title),
    ticketStatus: (readString(row.ticketStatus) ??
      'open') as PlatformAdminSupportThread['ticketStatus'],
    priority: (readString(row.priority) ??
      'normal') as PlatformAdminSupportThread['priority'],
    tags: readArray(row.tags).map((t) => String(t)),
    assignedAdmin: readString(row.assignedAdmin),
    subject: readString(row.subject),
    lastMessageAt: readString(row.lastMessageAt),
    lastMessagePreview: readString(row.lastMessagePreview),
    unreadForAdmin: readNumber(row.unreadForAdmin),
    createdAt: readString(row.createdAt) ?? '',
  };
}

function mapSupportAttachment(
  row: Record<string, unknown>,
): PlatformAdminSupportAttachment {
  return {
    id: readString(row.id) ?? '',
    storagePath: readString(row.storagePath) ?? '',
    fileName: readString(row.fileName) ?? '',
    mimeType: readString(row.mimeType),
    sizeBytes: row.sizeBytes != null ? readNumber(row.sizeBytes) : null,
    width: row.width != null ? readNumber(row.width) : null,
    height: row.height != null ? readNumber(row.height) : null,
  };
}

function mapSupportMessage(
  row: Record<string, unknown>,
): PlatformAdminSupportMessage {
  return {
    id: readString(row.id) ?? '',
    threadId: readString(row.threadId) ?? '',
    senderRole: (readString(row.senderRole) ??
      'user') as PlatformAdminSupportMessage['senderRole'],
    senderUserId: readString(row.senderUserId),
    body: readString(row.body) ?? '',
    createdAt: readString(row.createdAt) ?? '',
    attachments: readArray(row.attachments).map((a) =>
      mapSupportAttachment(asRecord(a)),
    ),
  };
}

function mapAppFeedbackItem(row: Record<string, unknown>): AppFeedbackItem {
  return {
    id: readString(row.id) ?? '',
    category: (readString(row.category) ??
      'other') as AppFeedbackItem['category'],
    rating: row.rating != null ? readNumber(row.rating) : null,
    message: readString(row.message) ?? '',
    name: readString(row.name),
    email: readString(row.email),
    appVersion: readString(row.appVersion),
    platform: readString(row.platform),
    deviceInfo: readString(row.deviceInfo),
    pageUrl: readString(row.pageUrl),
    source: readString(row.source) ?? 'landing',
    status: (readString(row.status) ?? 'new') as AppFeedbackItem['status'],
    adminNote: readString(row.adminNote),
    createdAt: readString(row.createdAt) ?? '',
    handledAt: readString(row.handledAt),
  };
}

function mapAdminCarrierRequest(
  row: Record<string, unknown>,
): AdminCarrierRequest {
  return {
    id: readString(row.id) ?? '',
    tenantId: readString(row.tenantId) ?? '',
    tenantName: readString(row.tenantName) ?? '',
    requestedBy: readString(row.requestedBy) ?? '',
    requesterEmail: readString(row.requesterEmail),
    carrierName: readString(row.carrierName) ?? '',
    websiteOrContact: readString(row.websiteOrContact),
    note: readString(row.note),
    status: (readString(row.status) ?? 'new') as AdminCarrierRequest['status'],
    merchantMessage: readString(row.merchantMessage),
    linkedProviderId: readString(row.linkedProviderId),
    linkedProviderName: readString(row.linkedProviderName),
    linkedProviderCode: readString(row.linkedProviderCode),
    statusUpdatedAt: readString(row.statusUpdatedAt) ?? '',
    completedAt: readString(row.completedAt),
    createdAt: readString(row.createdAt) ?? '',
    updatedAt: readString(row.updatedAt) ?? '',
    events: readArray(row.events).map((x) => {
      const e = asRecord(x);
      return {
        id: readNumber(e.id),
        eventType: readString(e.eventType) ?? '',
        fromStatus: readString(e.fromStatus) as
          | AdminCarrierRequest['status']
          | null,
        toStatus: readString(e.toStatus) as
          | AdminCarrierRequest['status']
          | null,
        message: readString(e.message),
        visibleToMerchant: readBoolean(e.visibleToMerchant),
        createdAt: readString(e.createdAt) ?? '',
      };
    }),
    internalNotes: readArray(row.internalNotes).map((x) => {
      const n = asRecord(x);
      return {
        id: readNumber(n.id),
        authorEmail: readString(n.authorEmail),
        body: readString(n.body) ?? '',
        createdAt: readString(n.createdAt) ?? '',
      };
    }),
  };
}

function mapSupportNote(
  row: Record<string, unknown>,
): PlatformAdminSupportNote {
  return {
    id: readString(row.id) ?? '',
    threadId: readString(row.threadId) ?? '',
    authorId: readString(row.authorId),
    authorEmail: readString(row.authorEmail),
    body: readString(row.body) ?? '',
    createdAt: readString(row.createdAt) ?? '',
  };
}

function narrow<T extends string>(
  value: unknown,
  allowed: readonly T[],
): T | null {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : null;
}

function mapTenantNote(
  record: Record<string, unknown>,
): PlatformAdminTenantNote | null {
  const id = readString(record.id);
  const tenantId = readString(record.tenantId);
  const body = readString(record.body);
  const happenedAt = readString(record.happenedAt);
  if (!id || !tenantId || !body || !happenedAt) return null;
  return {
    id,
    tenantId,
    kind: narrow(record.kind, CLIENT_NOTE_KIND_VALUES) ?? 'note',
    outcome: narrow(record.outcome, CLIENT_CALL_OUTCOME_VALUES),
    body,
    happenedAt,
    authorEmail: readString(record.authorEmail),
    createdAt: readString(record.createdAt) ?? happenedAt,
  };
}

function mapTenantFollowUp(
  record: Record<string, unknown>,
): PlatformAdminTenantFollowUp | null {
  const tenantId = readString(record.tenantId);
  const stage = narrow(record.stage, CLIENT_STAGE_VALUES);
  if (!tenantId || !stage) return null;
  const updatedAt = readString(record.updatedAt);
  return {
    tenantId,
    stage,
    nextActionAt: readString(record.nextActionAt),
    nextActionNote: readString(record.nextActionNote),
    stageChangedAt: readString(record.stageChangedAt) ?? updatedAt ?? '',
    updatedAt: updatedAt ?? '',
  };
}

function mapTenantCrmLink(
  record: Record<string, unknown>,
): PlatformAdminTenantCrmLink | null {
  const id = readString(record.id);
  const companyName = readString(record.companyName);
  if (!id || !companyName) return null;
  return {
    id,
    companyName,
    status: readString(record.status) ?? 'won',
    source: readString(record.source) ?? 'other',
    wonAt: readString(record.wonAt),
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function readArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function readBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value === 'true';
  return false;
}

function readNullableBoolean(value: unknown): boolean | null {
  if (value == null) return null;
  return readBoolean(value);
}
